/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { ChangeEvent, Component, FormEvent, MouseEvent } from 'react';
import { AppState } from '../../app.state';
import { User, _User, NoteAvail } from '../../_shared/interfaces/user';
import { Org } from '../../_shared/interfaces/org';
import { Alert, Checkbox, Modal, Switch, message } from 'antd';
import { withRouter, RouteComponentProps } from 'react-router';
import FormTitle from '../../_shared/form-title';
import styles from './notification-settings.styles';
import { Button } from '../../_shared/button';
import { cleanCopy, removeEmpty, stopProp, userHasRole } from '../../utils';
import SharedStyles from '../../_shared/styles';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { get, set } from 'lodash';
import { ModalInput } from '../../_shared/modal-input';
import { WithForm } from '../../_shared/form';
import colors from '../../_shared/colors';
import { trackEvent } from '../../_shared/services/analytics.service';
import eventTypes from '../../_shared/interfaces/api/eventTypes';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateUser: (
    user: User,
    shouldSetCurrent: boolean
  ) => Promise<User | undefined>;
  refreshUser: (
    email: string,
    setCurrent: boolean
  ) => Promise<User | undefined>;
  _user?: User;
  form?: any;
}

interface NotificationConfig {
  key: string;
  title: string;
}

interface NotificationGroup {
  title: string;
  type: NoteAvail;
  items: NotificationConfig[];
}

// AER-1496 (change request 2026-04-15): "Hide this option / toggle for users
// that are not 'Org' users associated specifically to a DENTAL org."
//
// Roles that can actually receive the monthly utilization email. Deliberately
// NARROWER than utils' isDentalOrgUser, which also accepts role 6 (Org Read
// Only): the sender — checklocations() in
// server/src/controllers/email/email-scheduler.ts — filters on
// [2, 3, 4].includes(u.role), so a role-6 user would be shown a toggle that
// can never fire. Do not widen this list to reuse isDentalOrgUser without
// widening the sender's role filter to match.
const REPORT_ROLES = [2, 3, 4];

const notificationGroups: NotificationGroup[] = [
  {
    title: 'Email Notifications',
    type: 'email',
    items: [
      { key: 'events', title: 'Minor Alerts' },
      { key: 'alerts', title: 'Major Alerts' },
      { key: 'service', title: 'Maintenance Alerts' },
      // Visibility is gated per user by canSeeReports(); see
      // renderNotificationGroup. 'reports' is intentionally email-only — it is
      // absent from the SMS and Push groups below, so the gate never has to
      // consider them.
      { key: 'reports', title: 'Monthly Utilization Reports' },
    ],
  },
  {
    title: 'SMS Notifications',
    type: 'sms',
    items: [
      { key: 'events', title: 'Minor Alerts' },
      { key: 'alerts', title: 'Major Alerts' },
      { key: 'service', title: 'Maintenance Alerts' },
    ],
  },
  {
    title: 'Push Notifications',
    type: 'push',
    items: [
      { key: 'events', title: 'Minor Alerts' },
      { key: 'alerts', title: 'Major Alerts' },
      { key: 'equipmentLeftOn', title: 'Equipment Left On' },
      { key: 'equipmentNotConnected', title: 'Equipment Not Connected' },
    ],
  },
];

interface IState {
  loading: boolean;
  checked: boolean;
  visible: boolean;
  phoneModalVisible: boolean;
  phone: string | undefined;
  notifications: {
    [key: string]: NoteAvail[];
  };
}

export class _NotificationSettings extends Component<IProps, IState> {
  state: IState = {
    loading: false,
    checked: false,
    visible: false,
    phoneModalVisible: false,
    phone: undefined,
    notifications: {
      events: [],
      alerts: [],
      service: [],
      reports: [],
      equipmentLeftOn: [],
      equipmentNotConnected: [],
    },
  };

  // AER-1545: race-condition fix for rapid toggling. Calling submit() directly
  // from handleToggle (per AER-1579) raced with the in-flight save: submit()
  // bails when state.loading is true, so taps 2..N during a save were dropped
  // and the success handler then overwrote local state from the (now-stale)
  // server response, making the user's later toggles appear to revert ON.
  // Debounce a single save off the trailing edge of a burst, and if a save is
  // already running when the timer fires, queue one more save once it returns.
  private saveTimer: number | null = null;
  private resaveAfter = false;

  // AER-1545: set to true the first time the user touches a toggle. Used to
  // suppress the on-mount server refresh from clobbering an in-progress edit
  // if the fetch completes after the user has already started interacting.
  private userInteracted = false;

  componentDidMount = () => {
    // Seed from redux for an immediate render, then fetch fresh so toggles
    // always reflect server-side truth (not a possibly-stale redux snapshot).
    this.loadUserNotifications();
    this.refreshFromServer();
  };

  refreshFromServer = async () => {
    const { _user, sState } = this.props;
    const email = _user ? _user.email : get(sState, 'auth.user.email');
    if (!email) { return; }
    const fresh = await this.props.refreshUser(email, !_user);
    if (!fresh) { return; }
    // If the user already toggled something while the fetch was in flight,
    // their local state is the source of truth — don't overwrite it.
    if (this.userInteracted) { return; }
    this.applyNotificationsFromUser(fresh);
  };

  applyNotificationsFromUser = (raw: User) => {
    const User = new _User({ ...raw });
    this.setState({
      notifications: {
        events: User.notification.events || [],
        alerts: User.notification.alerts || [],
        service: User.notification.service || [],
        reports: User.notification.reports || [],
        equipmentLeftOn: User.notification.equipmentLeftOn || [],
        equipmentNotConnected: User.notification.equipmentNotConnected || [],
      },
    });
  };

  componentWillUnmount() {
    if (this.saveTimer !== null) {
      window.clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
  }

  scheduleSave = () => {
    if (this.saveTimer !== null) {
      window.clearTimeout(this.saveTimer);
    }
    this.saveTimer = window.setTimeout(() => {
      this.saveTimer = null;
      if (this.state.loading) {
        this.resaveAfter = true;
        return;
      }
      this.submit();
    }, 400);
  };

  loadUserNotifications = () => {
    const {
      sState: {
        auth: { user },
      },
      _user,
    } = this.props;

    const User = _user
      ? new _User({ ..._user })
      : new _User({
          ...user,
        } as User);

    this.setState({
      notifications: {
        events: User.notification.events || [],
        alerts: User.notification.alerts || [],
        service: User.notification.service || [],
        reports: User.notification.reports || [],
        equipmentLeftOn: User.notification.equipmentLeftOn || [],
        equipmentNotConnected: User.notification.equipmentNotConnected || [],
      },
    });
  };

  renderModal = () => {
    return (
      <Modal
        title={'SMS Terms and Conditions'}
        open={this.state.visible}
        onOk={this.submit}
        onCancel={this.closeModal}
        okButtonProps={{ disabled: !this.state.checked }}
        destroyOnHidden={true}
      >
        <div
          css={css`
            display: flex;
            flex-direction: row;
          `}
        >
          <Checkbox
            onChange={this.onCheckTerms}
            css={css`
              margin-right: 10px;
            `}
            value={this.state.checked}
          />
          <div>
            <p>
              By checking this box, you agree to receive SMS notification
              messages regarding the status of your Dental EZ equipment from
              Dental EZ, Inc. to the mobile number provided for this account,
              and understand your consent is not a condition of purchase. You
              may receive up to two (2) automated text messages to complete your
              enrollment in this text alert program.
            </p>
            <p>
              Message & data rates may apply. To opt-out at any time, return to
              this screen to update your Notification Settings or text STOP in
              response to any message. Text HELP for help. Terms:{' '}
              <a
                href={'https://www.aerasintel.com/terms-of-use'}
                target="_blank"
              >
                https://www.aerasintel.com/terms-of-use
              </a>{' '}
              and Privacy Policy:{' '}
              <a
                href={'https://www.aerasintel.com/privacy-policy'}
                target="_blank"
              >
                https://www.aerasintel.com/privacy-policy.
              </a>
            </p>
          </div>
        </div>
      </Modal>
    );
  };

  openModal = () => this.setState({ visible: true });
  closeModal = () => this.setState({ visible: false });

  onCheckTerms = (e: CheckboxChangeEvent) => {
    this.setState({
      checked: e.target.checked,
    });
  };

  handleToggle = (
    notificationKey: string,
    type: NoteAvail,
    checked: boolean
  ) => {
    this.userInteracted = true;
    this.setState(
      prevState => {
        // AER-1545: strip null sentinels before computing the new value. The
        // server stores [null] for explicitly-off keys (round-tripped through
        // the _User constructor), so prev values may legitimately contain
        // null. We don't want those nulls to leak into the new state when
        // the user takes a positive action.
        const filtered = (prevState.notifications[notificationKey] || []).filter(
          v => !!v
        );
        const newValues: NoteAvail[] = checked
          ? filtered.includes(type) ? filtered : [...filtered, type]
          : filtered.filter(v => v !== type);

        return {
          notifications: {
            ...prevState.notifications,
            [notificationKey]: newValues,
          },
        };
      },
      () => {
        // AER-1579: persist on toggle — SMS still routes through the phone/terms
        // modals which already end in a submit() call on confirmation.
        if (this.getShowPhone()) {
          this.openPhoneModal();
        } else if (this.getShowTerms()) {
          this.openModal();
        } else {
          this.scheduleSave();
        }
      }
    );
  };

  isEnabled = (notificationKey: string, type: NoteAvail): boolean => {
    const values = this.state.notifications[notificationKey] || [];
    return values.includes(type);
  };

  submit = async (e?: MouseEvent | FormEvent) => {
    stopProp(e);
    if (this.state.loading) {
      return;
    }

    this.setState({ loading: true });

    try {
      const {
        sState: { auth },
        _user,
      } = this.props;
      const cUser = auth.user;
      const user = _user
        ? (new _User({ ..._user }) as User)
        : new _User({
            ...auth.user,
          } as User);

      const origUser = cleanCopy(user);

      const { notifications } = this.state;
      Object.keys(notifications).forEach(key => {
        const val = notifications[key];
        // @ts-ignore - null array is used to clear notifications on backend
        user.notification[key] = val.length === 0 ? [null] : val;
      });

      if (this.state.checked) {
        user.SMSTermsDate = new Date().toISOString();
      }
      if (this.state.phone) {
        set(user, 'phone', this.state.phone);
      }
      const shouldSetCurrent = !!(cUser && user.userId === cUser.userId);
      trackEvent(eventTypes.user_notifications, { old: origUser, new: user });
      // AER-1545: previously we re-seeded local notifications state from the
      // server response. That clobbered any toggle the user had flipped while
      // the save was in flight (the in-flight save's response was a stale
      // snapshot). The redux current-user dispatch inside updateUser still
      // refreshes the auth store, but we deliberately leave the form's local
      // notifications state alone so the user's latest taps survive.
      await this.props.updateUser(removeEmpty(user), shouldSetCurrent);

      message.success('Data saved successfully!');
      this.setState({
        loading: false,
        checked: false,
        phone: '',
      });
      this.closeModal();
    } catch (err) {
      message.error('Save failed: ' + get(err, 'message'));
      this.setState({ loading: false });
    } finally {
      // AER-1545: if the user toggled additional rows during this save, the
      // debounced timer would have set resaveAfter and bailed. Now that the
      // save is done, kick off another debounced save with the latest local
      // state so nothing is lost.
      if (this.resaveAfter) {
        this.resaveAfter = false;
        this.scheduleSave();
      }
    }
  };

  getShowTerms = () => {
    const {
      sState: {
        auth: { user },
      },
    } = this.props;
    if (get(user, 'SMSTermsDate')) {
      return false;
    }

    const { notifications } = this.state;
    const hasSms =
      (notifications.events && notifications.events.includes('sms')) ||
      (notifications.alerts && notifications.alerts.includes('sms')) ||
      (notifications.service && notifications.service.includes('sms'));

    return hasSms;
  };

  getShowPhone = () => {
    const {
      sState: {
        auth: { user },
      },
    } = this.props;
    if (get(user, 'phone')) {
      return false;
    }

    const { notifications } = this.state;
    const hasSms =
      (notifications.events && notifications.events.includes('sms')) ||
      (notifications.alerts && notifications.alerts.includes('sms')) ||
      (notifications.service && notifications.service.includes('sms'));

    return hasSms;
  };

  notificationsDisabled = (type?: NoteAvail, notificationKey?: string) => {
    const { sState, _user } = this.props;
    const isDZAdminOrTech = userHasRole([0, 1], sState);
    const values = notificationKey
      ? this.state.notifications[notificationKey]
      : [];

    if (_user && !isDZAdminOrTech && !type && !notificationKey) return true;
    if (
      (_user &&
        isDZAdminOrTech &&
        ((values && !values.includes('sms') && type === 'sms') ||
          (type === 'sms' && !values))) ||
      (_user && !isDZAdminOrTech)
    ) {
      return true;
    }
    return false;
  };

  // AER-1496: is the user these settings belong to allowed to see the Monthly
  // Utilization Reports toggle? The test is applied to the SUBJECT of the form —
  // the `_user` prop when an admin is editing someone else, otherwise the
  // signed-in user — because the toggle controls who receives the email, not
  // who is looking at the screen. That is also why utils' isDentalOrgUser is
  // not reused here: it always reads sState.auth.user, so on the manage-user
  // screen it would answer for the admin rather than the user being edited.
  canSeeReports = (): boolean => {
    const { sState, _user } = this.props;
    const subject: User | undefined = _user || get(sState, 'auth.user');
    if (!subject || !REPORT_ROLES.includes(subject.role)) {
      return false;
    }
    const orgs = get(sState, 'dash.orgs', []) as Org[];
    const subjectOrg = orgs.find(o => o.id === subject.orgId);
    // orgType 1 = Dental, 0 = Service (src/_shared/interfaces/org.ts). An orgId
    // that resolves to no org stays hidden — unknown is not provably dental.
    return !!subjectOrg && subjectOrg.orgType === 1;
  };

  onCancelPhone = () => {
    this.setState({ phoneModalVisible: false, phone: '' });
  };

  onSubmitPhone = async () => {
    this.closePhoneModal();
    const shouldShowTerms = this.getShowTerms();
    if (shouldShowTerms) {
      return this.openModal();
    }
    await this.submit();
  };

  openPhoneModal = () => this.setState({ phoneModalVisible: true });
  closePhoneModal = () => this.setState({ phoneModalVisible: false });

  onChangePhone = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({ phone: event.target.value });
  };

  renderNotificationGroup = (group: NotificationGroup) => {
    const {
      sState: {
        auth: { user },
      },
    } = this.props;

    // AER-1496: drop the Monthly Utilization Reports row for anyone who is not
    // an Org user of a dental org. No other group declares a 'reports' item, so
    // the SMS and Push groups are unaffected by this filter.
    const items = group.items.filter(
      item => item.key !== 'reports' || this.canSeeReports()
    );

    return (
      <div key={group.type} css={css(styles.groupContainer)}>
        <h3 css={css(styles.groupTitle)}>{group.title}</h3>
        {items.map(item => {
          const isChecked = this.isEnabled(item.key, group.type);
          const disabled = this.notificationsDisabled(group.type, item.key);
          const showWarning =
            group.type === 'sms' &&
            !disabled &&
            user &&
            !user.phone &&
            isChecked;

          return (
            <div
              key={`${group.type}-${item.key}`}
              css={css(styles.notificationRow)}
            >
              <span css={css(styles.notificationLabel)}>{item.title}</span>
              <Switch
                checked={isChecked}
                disabled={disabled}
                onChange={(checked: boolean) =>
                  this.handleToggle(item.key, group.type, checked)
                }
                css={showWarning && css(styles.warningSwitch)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    const {
      sState: {
        auth: { user },
      },
      _user,
    } = this.props;
    const { phone, phoneModalVisible } = this.state;

    return user ? (
      <div css={css(SharedStyles.formContainer, styles.container)}>
        <FormTitle title={`Notification Settings`} />

        <div css={css(styles.notificationsContainer)}>
          {notificationGroups.map(group => this.renderNotificationGroup(group))}
        </div>

        {!_user && !this.notificationsDisabled() && !user.phone && (
          <div css={css(styles.errorContainer)}>
            <span css={css(styles.error)}>
              User does not have a phone number stored. You will be required to
              enter a phone number to make any changes to SMS notification
              settings.
            </span>
          </div>
        )}

        {this.renderModal()}
        <ModalInput
          title={'Telephone Number Required'}
          content={
            'In order to enable SMS Notifications a telephone number is required.'
          }
          label={'Phone'}
          placeholder={'1234567890'}
          onCancel={this.onCancelPhone}
          onChange={this.onChangePhone}
          onSubmit={this.onSubmitPhone}
          value={phone}
          visible={phoneModalVisible}
        />
      </div>
    ) : (
      <div>No User Provided</div>
    );
  }
}

export const NotificationSettings = withRouter(_NotificationSettings);
